import { useSuspenseQuery } from "@apollo/client/react";
import { parseISO } from "date-fns";
import { FlaskConical, Play } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyIdsButton } from "~/components/copy-button";
import type { JobRun } from "~/components/job-log";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
} from "~/components/ui";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import {
  GetDipDraftEvidencesDocument,
  type GetDipDraftEvidencesQuery,
  type GetDipDraftEvidencesQueryVariables,
  GetDipEvidenceDraftsDocument,
  type GetDipEvidenceDraftsQuery,
  type GetDipEvidenceDraftsQueryVariables,
} from "~/features/dataImportProjects/gqls";
import { useDipResourceMutationGuard } from "~/features/dataImportProjects/hooks/useDipResourceMutationGuard";
import { useSubmitBatchEvidenceCommitsJob } from "~/features/dataImportProjects/hooks/useSubmitBatchEvidenceCommitsJob";
import { useTenant } from "~/features/tenant/context";
import { JsonDiff, JsonKeyValues, type JsonRecord } from "./BatchEvidencesJobJson";
import { JobLogDialog } from "./BatchEvidencesJobLogDialog";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

function EvidenceDraftsPanelContent({
  batchEvidencesJobId,
  data,
  refetch,
  canExecuteMutation,
}: {
  batchEvidencesJobId: string;
  data: GetDipEvidenceDraftsQuery;
  refetch: () => void;
  canExecuteMutation: () => boolean;
}) {
  const { t } = useTranslation();
  const { isReadOnly } = useDipLifecycle();
  const [logDialogJobRun, setLogDialogJobRun] = useState<JobRun | null>(null);
  const [runConfirmOpen, setRunConfirmOpen] = useState(false);
  const drafts = data.unifiedEvidenceDrafts;
  const parentEvidenceIds = drafts.map((draft) => draft.parentEvidenceId).filter((id): id is string => id != null);
  const { data: evidenceData } = useSuspenseQuery<GetDipDraftEvidencesQuery, GetDipDraftEvidencesQueryVariables>(
    GetDipDraftEvidencesDocument,
    { variables: { ids: parentEvidenceIds } },
  );
  const parentById = new Map(evidenceData.evidences.map((evidence) => [evidence.id, evidence]));
  const { submit, processing } = useSubmitBatchEvidenceCommitsJob({
    batchEvidencesJobId,
    refetch,
    setLogDialogJobRun,
    canExecute: canExecuteMutation,
  });
  const count = data.unifiedEvidenceDraftsAggregate.aggregate?.count ?? 0;
  const actionsDisabled = count === 0 || processing.value || isReadOnly;

  useEffect(() => {
    if (isReadOnly) setRunConfirmOpen(false);
  }, [isReadOnly]);

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-12-regular text-fg-3">
          {count} {t("model.evidence.modelNames")}
        </p>
        <div className="flex flex-wrap gap-2">
          <CopyIdsButton ids={drafts.map((draft) => draft.id)} />
          <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.dryRun")}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={actionsDisabled}
              onClick={() => {
                if (!canExecuteMutation()) return;
                submit(true);
              }}
            >
              <FlaskConical className="size-3.5" />
              {processing.value && processing.dryRun ? t("ui.processing") : t("ui.dryRun")}
            </Button>
          </DipReadOnlyTooltip>
          <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("feature.dataImportProjects.detail.operation.runJob")}>
            <Button
              type="button"
              size="sm"
              disabled={actionsDisabled}
              onClick={() => {
                if (!canExecuteMutation()) return;
                setRunConfirmOpen(true);
              }}
            >
              <Play className="size-3.5" />
              {processing.value && !processing.dryRun
                ? t("ui.processing")
                : t("feature.dataImportProjects.detail.operation.runJob")}
            </Button>
          </DipReadOnlyTooltip>
        </div>
      </div>

      {drafts.length === 0 ? (
        <p className="rounded-[var(--radius-md)] border border-border-default p-4 text-12-regular text-fg-3">
          {t("feature.common.noData")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
          <table className="w-full min-w-[920px] border-collapse text-12-regular">
            <thead className="bg-bg-subtle text-fg-2">
              <tr>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold"></th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                  {t("model.common.properties")}
                </th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                  {t("model.common.metadata")}
                </th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                  {t("model.common.createdAt")}
                </th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((draft) => {
                const isUpdate = draft.parentEvidenceId != null;
                const parent = draft.parentEvidenceId ? parentById.get(draft.parentEvidenceId) : undefined;
                return (
                  <tr key={draft.id} className="border-t border-border-default align-top first:border-t-0">
                    <td className="px-3 py-2">
                      <Badge tone={isUpdate ? "status-review" : "score-a"}>
                        {isUpdate ? t("ui.update") : t("ui.new")}
                      </Badge>
                    </td>
                    <td className="px-3 py-2">
                      {parent ? (
                        <JsonDiff before={parent.properties as JsonRecord} after={draft.properties as JsonRecord} />
                      ) : (
                        <JsonKeyValues value={draft.properties as JsonRecord} />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {parent ? (
                        <JsonDiff before={parent.metadata as JsonRecord} after={draft.metadata as JsonRecord} />
                      ) : (
                        <JsonKeyValues value={draft.metadata as JsonRecord} />
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-fg-2">
                      {t("date.long", { date: parseISO(draft.createdAt) })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <JobLogDialog jobRun={logDialogJobRun} onOpenChange={(open) => !open && setLogDialogJobRun(null)} />
      {!isReadOnly ? (
        <AlertDialog
          open={runConfirmOpen}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setRunConfirmOpen(open);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("feature.dataImportProjects.detail.operation.runJob")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("feature.dataImportProjects.detail.jobs.confirmEvidenceCommitsJobRun")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("ui.cancel")}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (!canExecuteMutation()) return;
                  submit(false);
                }}
              >
                {t("feature.dataImportProjects.detail.operation.runJob")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </section>
  );
}

export function EvidenceDraftsPanel({ batchEvidencesJobId }: { batchEvidencesJobId: string }) {
  const { t } = useTranslation();
  const { projectId } = useDipLifecycle();
  const { tenantId } = useTenant();
  const { canExecuteMutation, setAuthorized } = useDipResourceMutationGuard(`evidence-drafts:${batchEvidencesJobId}`);
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const { data, refetch } = useSuspenseQuery<GetDipEvidenceDraftsQuery, GetDipEvidenceDraftsQueryVariables>(
    GetDipEvidenceDraftsDocument,
    { variables: { tenantId, batchEvidencesJobId } },
  );
  const job = data.batchEvidencesJobs[0];
  const isAuthorized = job?.dataImportProjectId === projectId;
  setAuthorized(isAuthorized);

  if (!job || !isAuthorized) {
    return <p className="text-12-regular text-fg-3">{t("feature.common.noData")}</p>;
  }

  return (
    <EvidenceDraftsPanelContent
      batchEvidencesJobId={batchEvidencesJobId}
      data={data}
      refetch={() => void refetch()}
      canExecuteMutation={canExecuteMutation}
    />
  );
}
